// A multi-camera dangerous-waste record describes each camera's image under `cameras`, with
// the image's pixel size and the detections the unit made on it: one item box and any number
// of pictogram boxes, all in that image's own pixel coordinates. The record dialog draws these
// over the image and starts zoomed in on the item.

export const MIN_IMAGE_ZOOM = 1;
export const MAX_IMAGE_ZOOM = 10;

// The item fills this share of the cell when an image first opens, leaving a margin around it.
const INITIAL_ITEM_FILL = 0.9;

const CAMERA_LABELS = {
    top_left: 'Øverst venstre',
    top_right: 'Øverst højre',
    bottom_left: 'Nederst venstre',
    bottom_right: 'Nederst højre',
};

export const clampImageZoom = (value) => Math.min(MAX_IMAGE_ZOOM, Math.max(MIN_IMAGE_ZOOM, value));

const toBox = (box) => {
    if (!box || typeof box !== 'object') return null;
    const [x, y, w, h] = [box.x, box.y, box.w, box.h].map(Number);
    if (![x, y, w, h].every(Number.isFinite) || w <= 0 || h <= 0) return null;
    const confidence = Number(box.confidence);
    return { x, y, w, h, confidence: Number.isFinite(confidence) ? confidence : null };
};

// The item detection is the box to show; crop_box is what the unit cropped to, and only stands
// in when a record carries no item detection.
const toCameraDetections = (cameraName, camera) => {
    const width = Number(camera.width);
    const height = Number(camera.height);
    const itemBox = toBox(camera.item_box) || toBox(camera.crop_box);
    const pictogramBoxes = (Array.isArray(camera.pictogram_boxes) ? camera.pictogram_boxes : [])
        .map((pictogram) => {
            const box = toBox(pictogram);
            return box ? { ...box, label: String(pictogram.label || '').trim() } : null;
        })
        .filter(Boolean);

    return {
        cameraName,
        cameraLabel: CAMERA_LABELS[cameraName] || cameraName,
        width: Number.isFinite(width) && width > 0 ? width : null,
        height: Number.isFinite(height) && height > 0 ? height : null,
        itemBox,
        pictogramBoxes,
    };
};

const baseName = (path) => String(path || '').split('/').pop();

/**
 * The camera detections for each image path, in the same order, or null for an image no
 * camera entry names (every image of an older single-camera record).
 */
export function getImageDetections(record, imagePaths) {
    const cameras = record?.cameras && typeof record.cameras === 'object' ? record.cameras : {};
    const byImageName = new Map();
    Object.entries(cameras).forEach(([cameraName, camera]) => {
        if (camera && typeof camera === 'object' && camera.image) {
            byImageName.set(baseName(camera.image), toCameraDetections(cameraName, camera));
        }
    });
    return imagePaths.map((path) => byImageName.get(baseName(path)) || null);
}

// Boxes can only be placed on an image whose pixel size the record states.
export const canDrawDetections = (detections) => Boolean(
    detections
    && detections.width
    && detections.height
    && (detections.itemBox || detections.pictogramBoxes.length > 0)
);

/**
 * Zoom and pan that frame the item box when an image first opens, for an image fitted
 * (object-fit: contain) into a cell of cellWidth × cellHeight CSS pixels. The pan is in the
 * fitted image's CSS pixels before zoom — the space the dialog's stage translates in. The
 * zoom contains the whole box, which for an item is its longest edge filling the cell.
 * Returns null when there is no item box to frame.
 */
export function getInitialImageView(detections, cellWidth, cellHeight) {
    if (!detections?.itemBox || !detections.width || !detections.height) return null;
    if (!(cellWidth > 0) || !(cellHeight > 0)) return null;

    const { width, height, itemBox } = detections;
    const fitScale = Math.min(cellWidth / width, cellHeight / height);
    const zoom = clampImageZoom(Math.min(
        (INITIAL_ITEM_FILL * cellWidth) / (itemBox.w * fitScale),
        (INITIAL_ITEM_FILL * cellHeight) / (itemBox.h * fitScale),
    ));
    if (zoom <= MIN_IMAGE_ZOOM) return { zoom: MIN_IMAGE_ZOOM, pan: { x: 0, y: 0 } };

    const boxCenterX = itemBox.x + itemBox.w / 2;
    const boxCenterY = itemBox.y + itemBox.h / 2;
    return {
        zoom,
        pan: {
            x: -(boxCenterX - width / 2) * fitScale,
            y: -(boxCenterY - height / 2) * fitScale,
        },
    };
}

/** Confidence as a whole percentage ("95%"), or "" when the box carries none. */
export const formatDetectionConfidence = (confidence) => (
    Number.isFinite(confidence) ? `${Math.round(confidence * 100)}%` : ''
);

/**
 * Weight in whole grams below a kilogram ("89 g") and in kilograms from there, to at most two
 * decimals with a Danish decimal comma ("1,3 kg"). Null when the record has no weight.
 */
export function formatRecordWeight(weightGrams) {
    if (weightGrams == null || weightGrams === '') return null;
    const grams = Number(weightGrams);
    if (!Number.isFinite(grams)) return null;
    const wholeGrams = Math.round(grams);
    if (wholeGrams < 1000) return `${wholeGrams} g`;
    return `${(grams / 1000).toLocaleString('da-DK', { maximumFractionDigits: 2 })} kg`;
}
